import React, { Component } from 'react';
import { AlertTriangle, RotateCcw, Bug, ArrowRight } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { BaseErrorBoundary, ErrorFallbackProps } from '../../components/shared/BaseErrorBoundary';
import { DjayNarrator } from '../../mascot';
/**
 * QuestionErrorBoundary — Catches rendering errors in question renderers.
 *
 * Prevents renderer crashes from breaking the entire practice session.
 * Shows a friendly fallback UI with skip/reset functionality.
 * Includes dev-only debug panel with question ID, error, and stack trace.
 * Now uses BaseErrorBoundary for shared lifecycle logic (s106 P2-18).
 *
 * Co-located in practice/ domain (CL-1 s63).
 */
// ── Types ────────────────────────────────────────────────────
interface QuestionErrorBoundaryProps {
  children: React.ReactNode;
  questionId: string;
  onReset: () => void;
  onSkipQuestion?: () => void;
}
// ── Component ────────────────────────────────────────────────
export class QuestionErrorBoundary extends Component<QuestionErrorBoundaryProps> {
  renderFallback = ({
    error,
    errorInfo,
    onReset
  }: ErrorFallbackProps): React.ReactNode => {
    return <div role="alert" aria-live="assertive" className="w-full max-w-2xl mx-auto px-4 py-8" data-id="element-11785" data-mp="21s9f6g942g75">
        
        <Card className="p-8 border-2 border-amber-200 bg-amber-50/50 shadow-xl shadow-amber-100" data-id="element-11786" data-mp="1wty6i8ujybqi">
          {/* Header with Djay */}
          <div className="flex items-start gap-4 mb-6" data-id="element-11787" data-mp="1rt0fr7vh89de">
            <DjayNarrator emotion="confused" size="lg" className="flex-shrink-0" data-id="element-11788" data-mp="1mvifs6zft4hj" />
            
            <div className="flex-1" data-id="element-11789" data-mp="1ivhovmv5j0tl">
              <div className="flex items-center gap-2 mb-2" data-id="element-11790" data-mp="1iwjcsrx9nhsq">
                <AlertTriangle className="w-5 h-5 text-amber-600" data-id="element-11791" data-mp="1dwwyef5pbvic" />
                <h3 className="text-xl font-bold text-slate-900" data-id="element-11792" data-mp="3d997vczdex1p">
                  Deze vraag kon niet worden geladen
                </h3>
              </div>
              <p className="text-slate-700 leading-relaxed" data-id="element-11793" data-mp="387jsxm911z8c">
                Er ging iets mis bij het tonen van deze vraag. Je kunt de vraag
                overslaan of opnieuw proberen.
              </p>
            </div>
          </div>

          {/* Dev Debug Panel */}
          {import.meta.env?.DEV && error && <div className="mb-6 p-4 bg-rose-50 rounded-lg border border-rose-200" data-id="element-11794" data-mp="3ibqgumhwiz3t">
              <div className="flex items-center gap-2 mb-2" data-id="element-11795" data-mp="3ncwacmjdyqee">
                <Bug className="w-4 h-4 text-rose-600" data-id="element-11796" data-mp="ewkxzc6b12py" />
                <p className="text-xs font-bold text-rose-800" data-id="element-11797" data-mp="ev08i0yuk9yy">
                  Debug Info (dev only)
                </p>
              </div>
              <div className="space-y-2 text-xs font-mono text-rose-700" data-id="element-11798" data-mp="4xlqhwx9zd4j">
                <div data-id="element-11799" data-mp="9xhw20lmfcm7">
                  <span className="font-bold" data-id="element-11800" data-mp="9wowbjl2xndk">Question ID:</span>{' '}
                  {this.props.questionId}
                </div>
                <div data-id="element-11801" data-mp="2pnkwi0urfc4i">
                  <span className="font-bold" data-id="element-11802" data-mp="2pr8mwr4wrif0">Error:</span> {error.message}
                </div>
                {error.stack && <div className="mt-2 p-2 bg-rose-100 rounded overflow-x-auto" data-id="element-11803" data-mp="2n47pk2faywsw">
                    <pre className="text-[10px] whitespace-pre-wrap break-all" data-id="element-11804" data-mp="2n6asfn1k0kf1">
                      {error.stack}
                    </pre>
                  </div>}
                {errorInfo?.componentStack && <div className="mt-2 p-2 bg-rose-100 rounded overflow-x-auto" data-id="element-11805" data-mp="2w2s3kq9x4ias">
                    <div className="font-bold mb-1" data-id="element-11806" data-mp="2r6ulofj0b3vw">Component Stack:</div>
                    <pre className="text-[10px] whitespace-pre-wrap" data-id="element-11807" data-mp="23hid9hvi6zun">
                      {errorInfo.componentStack}
                    </pre>
                  </div>}
              </div>
            </div>}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-11808" data-mp="2d7l9rs1u2vxq">
            {this.props.onSkipQuestion && <Button onClick={() => {
            onReset();
            this.props.onSkipQuestion?.();
          }} variant="primary" size="xl" fullWidth className="shadow-lg shadow-indigo-200" data-id="element-11809" data-mp="28e8vaaqvc5ip">
              
                <ArrowRight className="w-5 h-5 mr-2" data-id="element-11810" data-mp="1fotipgjk24ko" />
                Sla deze vraag over
              </Button>}
            <Button onClick={onReset} variant="secondary" size="xl" fullWidth data-id="element-11811" data-mp="1tbs9s15ih94s">
              <RotateCcw className="w-5 h-5 mr-2" data-id="element-11812" data-mp="1yczko92yaofo" />
              Probeer opnieuw
            </Button>
          </div>
        </Card>
      </div>;
  };
  render(): React.ReactNode {
    return <BaseErrorBoundary logPrefix="Question Renderer" logContext={{
      questionId: this.props.questionId
    }} resetKey={this.props.questionId} fallback={this.renderFallback} onReset={this.props.onReset} respectStrictMode data-id="element-11813" data-mp="2842j134hwpb9">
        
        {this.props.children}
      </BaseErrorBoundary>;
  }
}